// Modale de transfert d'une notification/tâche à un collègue (WKF-013 §7.4) —
// « Plus d'options » : recherche parmi TOUS les collègues (gros tenants ~80 users),
// + motif + notification. Les collègues sont ordonnés « derniers utilisés » d'abord.
// Liée à un appel d'offres, la tâche ne part qu'à qui VOIT l'AO (décision PO n° 66) : le
// serveur refuse les autres en 422 TASK_ASSIGNEE_OUT_OF_TENDER, l'écran ne les propose pas.

import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Check } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
    Command,
    CommandEmpty,
    CommandInput,
    CommandItem,
    CommandList,
} from '@/components/ui/command'
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
    notificationErrorMessage,
    useNotificationMutations,
} from '@/hooks/useNotifications'
import type { Notification } from '@/hooks/useNotifications.types'
import { cn } from '@/lib/utils'

import { ColleagueAvatar } from './ColleagueAvatar'
import { lienDeLaNotification } from './lienDeLaNotification'
import { useDestinatairesDeLaTache } from './useDestinatairesDeLaTache'

interface TransferDialogProps {
    notification: Notification | null
    entityId: string | null
    onOpenChange: (open: boolean) => void
    onTransferred?: () => void
}

export function TransferDialog({
    notification,
    entityId,
    onOpenChange,
    onTransferred,
}: TransferDialogProps) {
    const { t } = useTranslation()
    const open = notification !== null
    // Ordonnés « derniers utilisés » d'abord ; recherche sur toute la liste — bornée à qui voit
    // l'AO que nomme le lien ENREGISTRÉ de la tâche (celui que le serveur juge).
    const { ordered, remember, retenu, enCours } = useDestinatairesDeLaTache(
        entityId,
        lienDeLaNotification(notification),
    )
    const { transfer } = useNotificationMutations(entityId)

    const [recipientId, setRecipientId] = useState('')
    // Part le choix ENCORE proposé : la liste de l'AO, arrivée après le clic, a pu l'écarter.
    const choisi = retenu(recipientId)
    // Tant que la liste de l'AO est en route, tous les collègues sont offerts : rien ne part.
    const pret = Boolean(choisi) && !enCours
    const [reason, setReason] = useState('')
    const [notifyPrevious, setNotifyPrevious] = useState(true)

    useEffect(() => {
        if (open) {
            setRecipientId('')
            setReason('')
            setNotifyPrevious(true)
        }
    }, [open, notification?.id])

    async function submit() {
        if (!notification || !pret) return
        try {
            await transfer.mutateAsync({
                id: notification.id,
                payload: {
                    recipient_id: choisi,
                    reason: reason.trim() || null,
                    notify_previous: notifyPrevious,
                },
            })
            remember(choisi)
            toast.success(t('notifications.transfer.done', 'Tâche transférée.'))
            onTransferred?.()
            onOpenChange(false)
        } catch (err) {
            toast.error(notificationErrorMessage(err, t))
        }
    }

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[480px]">
                <DialogHeader>
                    <DialogTitle>{t('notifications.transfer.title', 'Transférer la tâche')}</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 py-2">
                    <div className="space-y-1.5">
                        <Label>{t('notifications.transfer.to', 'Transférer à')}</Label>
                        {/* Recherche parmi tous les collègues (gros tenants) ; derniers utilisés en tête. */}
                        <Command className="rounded-md border">
                            <CommandInput
                                placeholder={t('notifications.transfer.searchPlaceholder', 'Rechercher un collègue…')}
                            />
                            <CommandList className="max-h-52">
                                <CommandEmpty>
                                    {t('notifications.transfer.noColleague', 'Aucun collègue trouvé.')}
                                </CommandEmpty>
                                {ordered.map((c) => {
                                    const label = c.full_name || c.email
                                    return (
                                        <CommandItem
                                            key={c.id}
                                            value={`${label} ${c.email}`}
                                            onSelect={() => setRecipientId(c.id)}
                                        >
                                            <ColleagueAvatar colleague={c} />
                                            <span className="truncate">{label}</span>
                                            <Check
                                                className={cn(
                                                    'ml-auto h-4 w-4',
                                                    choisi === c.id ? 'opacity-100' : 'opacity-0',
                                                )}
                                            />
                                        </CommandItem>
                                    )
                                })}
                            </CommandList>
                        </Command>
                    </div>
                    <div className="space-y-1.5">
                        <Label htmlFor="transfer-reason">
                            {t('notifications.transfer.reason', 'Motif (facultatif)')}
                        </Label>
                        <Textarea
                            id="transfer-reason"
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                            rows={2}
                        />
                    </div>
                    <label className="flex items-center gap-2 text-sm">
                        <Checkbox
                            checked={notifyPrevious}
                            onCheckedChange={(v) => setNotifyPrevious(Boolean(v))}
                        />
                        {t('notifications.transfer.notifyPrevious', 'Garder une trace pour moi')}
                    </label>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)} disabled={transfer.isPending}>
                        {t('common.cancel', 'Annuler')}
                    </Button>
                    <Button onClick={submit} disabled={transfer.isPending || !pret}>
                        {t('notifications.actions.transfer', 'Transférer')}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}
